import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Tag, Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { useGooglePlaces, type Prediction } from "@/hooks/useGooglePlaces";
import { motion } from "framer-motion";

const BookingWidget = () => {
  const navigate = useNavigate();
  const [routeType, setRouteType] = useState<"one-way" | "round-trip">("one-way");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromPlaceId, setFromPlaceId] = useState("");
  const [toPlaceId, setToPlaceId] = useState("");
  const [passengers, setPassengers] = useState("2");
  const [promoCode, setPromoCode] = useState("");

  const fromPlaces = useGooglePlaces();
  const toPlaces = useGooglePlaces();

  const isAirportRoute = fromPlaceId.startsWith("db-airport-") || toPlaceId.startsWith("db-airport-");
  const maxPassengers = isAirportRoute ? 50 : 10;

  useEffect(() => {
    if (parseInt(passengers) > maxPassengers) {
      setPassengers(String(maxPassengers));
    }
  }, [maxPassengers]);

  const handleFromSelect = (prediction: Prediction) => {
    setFrom(prediction.description);
    setFromPlaceId(prediction.place_id);
    fromPlaces.setPredictions([]);
  };

  const handleToSelect = (prediction: Prediction) => {
    setTo(prediction.description);
    setToPlaceId(prediction.place_id);
    toPlaces.setPredictions([]);
  };

  const handleSearch = () => {
    navigate("/vehicles", {
      state: {
        from, to, fromPlaceId, toPlaceId, routeType,
        passengers: parseInt(passengers),
        promoCode,
      },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="w-full bg-white rounded-none shadow-2xl border border-border/30 p-4 md:p-6"
    >
      {/* Desktop: horizontal row */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        {/* From */}
        <div className="flex-1 min-w-0">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Pick-up</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
              <MapPin className="w-4 h-4 text-primary" />
            </div>
            <PlaceAutocomplete
              placeholder="Airport, Hotel or Address"
              value={from}
              onChange={setFrom}
              onSelect={handleFromSelect}
              predictions={fromPlaces.predictions}
              loading={fromPlaces.loading}
              onSearch={fromPlaces.search}
              iconColor="hidden"
            />
          </div>
        </div>

        {/* To */}
        <div className="flex-1 min-w-0">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Drop-off</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
              <MapPin className="w-4 h-4 text-secondary" />
            </div>
            <PlaceAutocomplete
              placeholder="Hotel, Address or Airport"
              value={to}
              onChange={setTo}
              onSelect={handleToSelect}
              predictions={toPlaces.predictions}
              loading={toPlaces.loading}
              onSearch={toPlaces.search}
              iconColor="hidden"
            />
          </div>
        </div>

        {/* Route Type */}
        <div className="lg:w-36">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Trip</label>
          <Select value={routeType} onValueChange={(v) => setRouteType(v as "one-way" | "round-trip")}>
            <SelectTrigger className="h-11 bg-white border-border rounded-none text-foreground text-sm font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="one-way">One Way</SelectItem>
              <SelectItem value="round-trip">Round Trip</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Passengers */}
        <div className="lg:w-32">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Passengers</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <Select value={passengers} onValueChange={setPassengers}>
              <SelectTrigger className="pl-10 h-11 bg-white border-border rounded-none text-foreground text-sm font-medium">
                <SelectValue placeholder="Pax" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxPassengers }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Promo Code */}
        <div className="lg:w-36">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Promo</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
              <Tag className="w-4 h-4 text-secondary" />
            </div>
            <input
              placeholder="Code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex h-11 w-full rounded-none border border-border bg-white px-3 pl-10 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 transition-colors"
            />
          </div>
        </div>

        {/* Search Button */}
        <div className="lg:w-auto">
          <label className="text-xs font-semibold text-transparent uppercase tracking-wider mb-1.5 hidden lg:block">Go</label>
          <Button
            onClick={handleSearch}
            className="w-full lg:w-auto h-11 px-8 text-sm font-bold gap-2 rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all"
          >
            <Search className="w-4 h-4" />
            Search
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default BookingWidget;
